/**
 * Konfiguracja płac (spec 01 §12a — kafel „Parametry płacowe", spec 01 §9 —
 * link „Uzupełnij dane w konfiguracji PPK").
 *
 * Ekran robi dwie rzeczy, bez których silnik naliczeń nie jest kompletny:
 *  1. pokazuje parametry roku z podstawą prawną i pozwala uzupełnić to, czego
 *     nie da się wpisać za firmę — składkę wypadkową (ustala ją ZUS dla
 *     płatnika) oraz kwoty ogłaszane na dany rok — a potem je POTWIERDZIĆ,
 *  2. zbiera realne deklaracje pracowników (PPK, PIT-2, koszty, zlecenie),
 *     żeby naliczenie nie musiało zgadywać (ADR-0010).
 *
 * Zapis ma rola płacowa; dział HR widzi te same dane bez formularzy [D28].
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  StatusPill,
  useToast,
} from '@k1p4/ui'
import { formatPLN } from '@k1p4/i18n'
import {
  KUP_VARIANTS,
  PPK_STATUSES,
  TAX_CREDIT_DIVISORS,
  TAX_RESIDENCES,
  AUTHOR_KUP_BP,
  isEmploymentContract,
  isPpkCovered,
  type ContractType,
  TAX_OFFICES,
  TAX_RELIEF_KINDS,
  ZUS_TERM_CODES,
  ZUS_TERM_CODE_PAYER,
  isZusTermCode,
  zusDeadlineDayFor,
  type ZusDeadlineDays,
  type ZusTermCode,
  INSURANCE_SCOPES,
  type InsuranceScope,
} from '@k1p4/shared'
import type {
  KupVariant,
  PpkStatus,
  TaxCreditDivisor,
  TaxReliefKind,
  TaxResidence,
} from '@k1p4/shared'
import { api, ApiError, type PayrollProfileItem, type PayrollSettingsInput, apiYear } from '../../api/client'
import { grToZloty, zlotyToGr } from '../../components/money'
import { useHasPermission } from '../../auth/AuthProvider'
import styles from './PayrollSettings.module.css'

/** Stawka w punktach bazowych jako procent do wyświetlenia: 167 → „1,67". */
function bpToPercent(bp: number | null): string {
  if (bp === null) return ''
  return (bp / 100).toFixed(2).replace('.', ',')
}

/** Procent z formularza na punkty bazowe; puste pole = brak wartości. */
function percentToBp(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (normalized === '') return null
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) return null
  return Math.round(parsed * 100)
}

/** Dzień wypłaty musi być dniem miesiąca 1–28 (art. 85 § 2 k.p. + CHECK w bazie). */
function payoutDayOf(value: string): number | null {
  const parsed = Number(value.trim())
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 28) return null
  return parsed
}

interface SettingsForm {
  accident: string
  payoutDay: string
  payoutNextMonth: boolean
  deviation: string
  cap: string
  minimumWage: string
  /** Kwoty wolne od potrąceń z zasiłków (art. 141 ustawy emerytalnej, Etap 36). */
  benefitFreeAlimony: string
  benefitFreeOther: string
  /** Okno pory nocnej: osiem godzin między 21:00 a 7:00 (Etap 42). */
  nightFrom: string
  nightTo: string
  /** Czy firma sama wypłaca zasiłki (art. 61 ustawy zasiłkowej). */
  benefitPayer: boolean
  benefitPayerFee: boolean
}

/**
 * Rodzaj umowy, z którego serwer liczy tytuł do ubezpieczeń i waliduje zakres:
 * warunki obowiązujące dziś, a nie ostatnio wpisane (przegląd Fali 2).
 */
function insuranceContractOf(profile: PayrollProfileItem): string | null {
  return profile.insuranceContractType ?? profile.contractType
}

/**
 * Koszty uzyskania w liście deklaracji według umowy (grupa 126): wariant
 * pracowniczy tylko przy stosunku pracy, przy zleceniu i dziele 20 % albo 50 %
 * z art. 22 ust. 9, u nierezydenta brak (podatek u źródła, art. 29), a przy B2B
 * nie dotyczy.
 */
function kupLabel(
  t: ReturnType<typeof useTranslation>['t'],
  profile: PayrollProfileItem,
  mandateKupBp: number,
): string {
  const type = insuranceContractOf(profile)
  if (type === 'b2b') return t('hrPayrollSettings.kupNotApplicable')
  if (type === 'zlecenie' || type === 'dzielo') {
    if (profile.taxResidence === 'nierezydent') return t('hrPayrollSettings.kupWithholding')
    /* Umowa do progu roku: ryczałt od przychodu, bez kosztów (art. 30 ust. 1 pkt 5a). */
    if (profile.flatRateTax === true) return t('hrPayrollSettings.kupFlatRate')
    /* Stawka w etykiecie bez zbędnych zer: „50 %", a nie „50,00 %". */
    const rate = (bp: number): string => String(bp / 100).replace('.', ',')
    return profile.authorCosts === true
      ? t('hrPayrollSettings.kupAuthor', { rate: rate(AUTHOR_KUP_BP) })
      : t('hrPayrollSettings.kupMandate', { rate: rate(mandateKupBp) })
  }
  return t(`hrPayrollSettings.kupVariant.${profile.kupVariant}`)
}

/** Pole zakresu ubezpieczenia widać przy zleceniu bez statusu ucznia albo studenta (#80). */
function scopeVisible(profile: PayrollProfileItem): boolean {
  return insuranceContractOf(profile) === 'zlecenie' && !profile.mandateStudentExempt
}

/** Zlecenie „tylko zdrowotne" — dobrowolnej chorobowej wtedy nie ma (art. 11 ust. 2). */
function healthOnlyVisible(profile: PayrollProfileItem): boolean {
  return scopeVisible(profile) && profile.insuranceScope === 'tylko_zdrowotne'
}

/** Pola okna deklaracji, przy których stoi błąd z API (przegląd Fali 3). */
const PROFILE_FIELD_ERRORS: readonly string[] = [
  'ppkEmployeeBp',
  'ppkEmployerBp',
  'insuranceCode',
  'occupationCode',
  'sickPayDaysBefore',
  'taxReliefUntilYear',
  'residenceCountry',
  'foreignTin',
  'treatyTaxBp',
  'residenceCertificateTo',
]

/** Odmowy zapisu zakresu ubezpieczenia — każda z własnym zdaniem. */
const SCOPE_ERRORS: Readonly<Record<string, string>> = {
  INSURANCE_SCOPE_NOT_APPLICABLE: 'hrPayrollSettings.errorInsuranceScope',
  INSURANCE_SCOPE_LOCKED: 'hrPayrollSettings.errorInsuranceScopeLocked',
  INSURANCE_SCOPE_SICKNESS: 'hrPayrollSettings.errorInsuranceScopeSickness',
}

/**
 * Kod terminu z rodzajem płatnika i dniem terminu z parametrów roku — tak, żeby
 * wybór kodu był zarazem wyborem terminu składek (art. 47 ust. 1 u.s.u.s., #10).
 */
function termCodeLabel(
  t: ReturnType<typeof useTranslation>['t'],
  code: ZusTermCode,
  days: ZusDeadlineDays,
): string {
  return t(`hrPayrollSettings.termCodes.${ZUS_TERM_CODE_PAYER[code]}`, {
    code,
    day: zusDeadlineDayFor(code, days),
  })
}

export function PayrollSettings() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = useHasPermission('Płace : Zatwierdzanie')
  const ids = {
    accident: useId(),
    payoutDay: useId(),
    deviation: useId(),
    cap: useId(),
    minimum: useId(),
    benefitFreeAlimony: useId(),
    benefitFreeOther: useId(),
    nightFrom: useId(),
    nightTo: useId(),
    ppkStatus: useId(),
    ppkEmployee: useId(),
    ppkEmployer: useId(),
    kup: useId(),
    divisor: useId(),
    nip: useId(),
    regon: useId(),
    taxOfficeCode: useId(),
    termCode: useId(),
    insuranceScope: useId(),
    bankAccount: useId(),
    zusAccount: useId(),
    taxAccount: useId(),
    ppkAccount: useId(),
    insuranceCode: useId(),
    taxRelief: useId(),
    taxReliefYear: useId(),
    occupationCode: useId(),
    sickDays: useId(),
    /* Rezydencja podatkowa i certyfikat (Etap 48). */
    taxResidence: useId(),
    residenceCountry: useId(),
    foreignTin: useId(),
    certificateFrom: useId(),
    certificateTo: useId(),
    treatyRate: useId(),
    payerBanner: useId(),
    profileBanner: useId(),
  }

  const year = apiYear()
  const [form, setForm] = useState<SettingsForm | null>(null)
  const [payerForm, setPayerForm] = useState<{
    nip: string
    regon: string
    /* Kod urzędu skarbowego płatnika do deklaracji rocznych (Etap 28). */
    taxOfficeCode: string
    termCode: string
    /* Rachunki do pliku przelewów (Etap 19). */
    bankAccount: string
    zusAccount: string
    taxAccount: string
    ppkAccount: string
  } | null>(null)
  const [editing, setEditing] = useState<PayrollProfileItem | null>(null)
  const [error, setError] = useState<string | null>(null)
  /*
   * Błędy przy polu (grupa 82): odmowa zapisu płatnika stoi w jego sekcji,
   * a odmowa profilu w oknie — z nazwami pól z API, nie jednym banerem u góry.
   */
  const [payerError, setPayerError] = useState<{ key: string; fields: readonly string[] } | null>(
    null,
  )
  const [profileFields, setProfileFields] = useState<readonly string[]>([])
  const [profileError, setProfileError] = useState<string | null>(null)
  /* Zamknięcie okna zabiera jego odmowę — nie zostaje banerem na stronie. */
  const closeProfile = (): void => {
    setEditing(null)
    setProfileError(null)
    setProfileFields([])
  }

  const parameters = useQuery({
    queryKey: ['payroll', 'parameters', year],
    queryFn: () => api.payrollParameters(year),
  })
  const profiles = useQuery({ queryKey: ['payroll', 'profiles'], queryFn: () => api.payrollProfiles() })

  /*
   * Ciało żądania składa handler i przekazuje je do `mutate` — nie czytamy tu
   * stanu formularza. `setForm` jest asynchroniczne, więc mutacja odczytująca
   * `form` z domknięcia widziała wartość z POPRZEDNIEGO renderu: przy pierwszej
   * wizycie (form === null) zapis wysyłał `null` i kasował wcześniej wpisaną
   * składkę wypadkową oraz kwoty roku.
   */
  const saveSettings = useMutation({
    mutationFn: (input: { body: PayrollSettingsInput }) =>
      api.savePayrollSettings(year, input.body),
    onSuccess: () => {
      toast.show(t('hrPayrollSettings.saved'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
      void queryClient.invalidateQueries({ queryKey: ['settings', 'tiles'] })
    },
    /* Okno pory nocnej poza art. 151(7) § 1 ma powiedzieć, CZEGO brakuje (Etap 42). */
    onError: (cause) =>
      setError(
        cause instanceof ApiError && cause.code === 'NIGHT_WINDOW_INVALID'
          ? 'hrPayrollSettings.errorNightWindow'
          : 'hrPayrollSettings.errorGeneric',
      ),
  })

  /*
   * Dane płatnika idą dwiema trasami, bo dwie rzeczy: REGON to atrybut firmy,
   * a kod terminu przysyłania deklaracji jest ustawieniem roku płacowego.
   */
  const savePayer = useMutation({
    mutationFn: async (values: {
      nip: string
      regon: string
      taxOfficeCode: string
      termCode: string
      bankAccount: string
      zusAccount: string
      taxAccount: string
      ppkAccount: string
    }) => {
      const optional = (value: string) => (value.trim() === '' ? null : value.trim())
      await api.savePayrollPayer({
        nip: optional(values.nip),
        regon: optional(values.regon),
        taxOfficeCode: optional(values.taxOfficeCode),
        /* Rachunki do pliku przelewów — puste pole znaczy „nie wpisano". */
        bankAccount: optional(values.bankAccount),
        zusAccount: optional(values.zusAccount),
        taxAccount: optional(values.taxAccount),
        ppkAccount: optional(values.ppkAccount),
      })
      /*
       * Kod terminu wysyłamy tylko wtedy, gdy się zmienił na kod z mapy albo na
       * „brak". Kod zapisany przed #10 (spoza mapy) nie może blokować zapisu
       * NIP-u i rachunków — serwer by go odrzucił po zapisie pierwszej połowy.
       */
      const code = values.termCode.trim()
      const savedCode = parameters.data?.settings.zusTermCode ?? ''
      if (code !== savedCode && (code === '' || isZusTermCode(code))) {
        await api.savePayrollSettings(year, { zusTermCode: code === '' ? null : code })
      }
    },
    onSuccess: () => {
      setPayerError(null)
      toast.show(t('hrPayrollSettings.payerSaved'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
      void queryClient.invalidateQueries({ queryKey: ['declarations'] })
    },
    onError: (cause) => {
      const fields = cause instanceof ApiError && cause.code === 'VALIDATION_ERROR' ? cause.issues : []
      revealError(fields, ids.payerBanner)
      setPayerError({
        key:
          fields.length === 0
            ? 'hrPayrollSettings.errorGeneric'
            : fields.every((field) => field === 'nip' || field === 'regon')
              ? 'hrPayrollSettings.errorPayer'
              : 'hrPayrollSettings.errorPayerFields',
        fields,
      })
      /* Część zapisu mogła przejść — ekran ma pokazać stan z serwera. */
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
  })

  const saveProfile = useMutation({
    mutationFn: (profile: PayrollProfileItem) =>
      api.savePayrollProfile(profile.employeeId, {
        ppkStatus: profile.ppkStatus,
        ppkEmployeeBp: profile.ppkEmployeeBp,
        ppkEmployerBp: profile.ppkEmployerBp,
        kupVariant: profile.kupVariant,
        taxCreditDivisor: profile.taxCreditDivisor,
        youthReliefWaived: profile.youthReliefWaived,
        /* Ulga z art. 21 ust. 1 pkt 152–154 na wniosek pracownika (Etap 32). */
        taxReliefKind: profile.taxReliefKind,
        taxReliefUntilYear: profile.taxReliefUntilYear,
        /* Przy zakresie „tylko zdrowotne" dobrowolnej chorobowej nie ma (art. 11 ust. 2). */
        mandateSicknessOptIn:
          profile.mandateSicknessOptIn && !healthOnlyVisible(profile),
        mandateStudentExempt: profile.mandateStudentExempt,
        /*
         * Zakres ubezpieczenia (#80) idzie tylko wtedy, gdy pole jest widoczne:
         * ukryte pole z wartością zapisaną wcześniej nie może blokować zapisu
         * PPK albo PIT-2 po aneksie czy po zaznaczeniu statusu studenta.
         */
        ...(scopeVisible(profile) ? { insuranceScope: profile.insuranceScope ?? 'pelne' } : {}),
        /*
         * Te pola były edytowane w modalu, ale nie wchodziły do żądania —
         * użytkownik wpisywał kod tytułu ubezpieczenia albo dni ze świadectwa
         * pracy i po zapisie znikały. Wysyłamy cały stan formularza.
         */
        insuranceCode: profile.insuranceCode,
        occupationCode: profile.occupationCode,
        sickPayDaysBefore: profile.sickPayDaysBefore,
        sickPayDaysBeforeYear: profile.sickPayDaysBeforeYear,
        benefitFromFirstDay: profile.benefitFromFirstDay,
        /*
         * Koszty autorskie 50 % (art. 22 ust. 9 pkt 3 PIT, Etap 46): pole było
         * w modalu, ale nie wchodziło do żądania, a API zapisywało wtedy
         * `false` — każdy zapis modalu kasował flagę i pasek liczył 20 % (#11).
         */
        authorCosts: profile.authorCosts === true,
        /*
         * Rezydencja podatkowa i certyfikat (Etap 48). Numeru podatnika nie
         * odsyłamy, gdy nikt go nie zmienił: z API wraca MASKA, więc jej
         * zapis nadpisałby numer kropkami.
         */
        taxResidence: profile.taxResidence ?? 'rezydent',
        residenceCountry: profile.residenceCountry ?? null,
        ...(typeof profile.foreignTin === 'string' && profile.foreignTin.includes('•')
          ? {}
          : { foreignTin: profile.foreignTin ?? null }),
        residenceCertificateFrom: profile.residenceCertificateFrom ?? null,
        residenceCertificateTo: profile.residenceCertificateTo ?? null,
        treatyTaxBp: profile.treatyTaxBp ?? null,
      }),
    onSuccess: () => {
      toast.show(t('hrPayrollSettings.profileSaved'), { tone: 'success' })
      closeProfile()
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'profiles'] })
    },
    onError: (cause) => {
      const fields = cause instanceof ApiError && cause.code === 'VALIDATION_ERROR' ? cause.issues : []
      setProfileFields(fields)
      revealError(fields, ids.profileBanner)
      /* „Popraw zaznaczone pola" tylko wtedy, gdy któreś pole rzeczywiście jest zaznaczone. */
      setProfileError(
        cause instanceof ApiError && cause.code in SCOPE_ERRORS
          ? SCOPE_ERRORS[cause.code]!
          : fields.some((field) => PROFILE_FIELD_ERRORS.includes(field))
            ? 'hrPayrollSettings.errorProfileFields'
            : 'hrPayrollSettings.errorGeneric',
      )
    },
  })

  /*
   * Po odmowie fokus idzie na pierwsze pole z błędem — przy zapisie z dołu okna
   * albo sekcji pole i baner były poza widokiem (weryfikacja Fali 3, grupa 82).
   * Bez zaznaczonego pola przewijamy do banera.
   */
  const FIELD_IDS: Record<string, string> = {
    nip: ids.nip,
    regon: ids.regon,
    taxOfficeCode: ids.taxOfficeCode,
    bankAccount: ids.bankAccount,
    zusAccount: ids.zusAccount,
    taxAccount: ids.taxAccount,
    ppkAccount: ids.ppkAccount,
    ppkEmployeeBp: ids.ppkEmployee,
    ppkEmployerBp: ids.ppkEmployer,
    insuranceCode: ids.insuranceCode,
    occupationCode: ids.occupationCode,
    sickPayDaysBefore: ids.sickDays,
    taxReliefUntilYear: ids.taxReliefYear,
    residenceCountry: ids.residenceCountry,
    foreignTin: ids.foreignTin,
    treatyTaxBp: ids.treatyRate,
    residenceCertificateTo: ids.certificateTo,
  }
  const revealError = (fields: readonly string[], bannerId: string): void => {
    globalThis.setTimeout(() => {
      const field = fields
        .map((name) => (FIELD_IDS[name] ? document.getElementById(FIELD_IDS[name]!) : null))
        .find((element) => element !== null)
      if (field) {
        field.focus()
        return
      }
      document.getElementById(bannerId)?.scrollIntoView?.({ block: 'nearest' })
    }, 0)
  }

  /** Pole okna deklaracji: błąd z API zamiast podpowiedzi (przegląd Fali 3). */
  const profileField = (field: string, hint: string): { error: string } | { hint: string } =>
    profileFields.includes(field)
      ? { error: t(`hrPayrollSettings.fieldErrors.${field}`) }
      : { hint: t(hint) }

  /** Pole płatnika: błąd z API zamiast podpowiedzi, gdy serwer je wskazał. */
  const payerField = (field: string, hint: string): { error: string } | { hint: string } =>
    payerError?.fields.includes(field)
      ? { error: t(`hrPayrollSettings.fieldErrors.${field}`) }
      : { hint: t(hint) }

  if (parameters.isPending || profiles.isPending) return <Spinner label={t('common.loading')} />
  if (parameters.isError || profiles.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const data = parameters.data
  const current: SettingsForm =
    form ?? {
      accident: bpToPercent(data.settings.accidentBp),
      payoutDay: String(data.settings.payoutDay),
      payoutNextMonth: data.settings.payoutNextMonth,
      deviation: bpToPercent(data.settings.deviationAlertBp),
      cap: grToZloty(data.parameters.annualSocialCapGr),
      minimumWage: grToZloty(data.parameters.minimumWageGr),
      benefitFreeAlimony: grToZloty(data.parameters.benefitFreeAlimonyGr),
      benefitFreeOther: grToZloty(data.parameters.benefitFreeOtherGr),
      nightFrom: data.settings.nightFrom,
      nightTo: data.settings.nightTo,
      benefitPayer: data.settings.benefitPayer,
      benefitPayerFee: data.settings.benefitPayerFee,
    }

  /** Dane płatnika: REGON firmy i kod terminu przysyłania deklaracji. */
  const payer =
    payerForm ?? {
      nip: data.payer.nip ?? '',
      regon: data.payer.regon ?? '',
      taxOfficeCode: data.payer.taxOfficeCode ?? '',
      termCode: data.settings.zusTermCode ?? '',
      /* Rachunki do pliku przelewów (Etap 19) — bez nich plik nie powstaje. */
      bankAccount: data.payer.bankAccount ?? '',
      zusAccount: data.payer.zusAccount ?? '',
      taxAccount: data.payer.taxAccount ?? '',
      ppkAccount: data.payer.ppkAccount ?? '',
    }

  /**
   * Zapisujemy WSZYSTKIE pola z formularza, także nietknięte — inaczej zapis
   * jednej wartości zerowałby pozostałe. Puste pole znaczy „nie wpisano" i tak
   * właśnie leci do API (`null`), bo silnik potrafi z tym żyć i to zgłasza.
   */
  function settingsBody(values: SettingsForm, confirm?: boolean): PayrollSettingsInput | null {
    const payoutDay = payoutDayOf(values.payoutDay)
    if (payoutDay === null) return null
    return {
      accidentBp: percentToBp(values.accident),
      payoutDay,
      payoutNextMonth: values.payoutNextMonth,
      deviationAlertBp: percentToBp(values.deviation) ?? data.settings.deviationAlertBp,
      annualSocialCapGr: zlotyToGr(values.cap),
      minimumWageGr: zlotyToGr(values.minimumWage),
      benefitFreeAlimonyGr: zlotyToGr(values.benefitFreeAlimony),
      benefitFreeOtherGr: zlotyToGr(values.benefitFreeOther),
      nightFrom: values.nightFrom,
      nightTo: values.nightTo,
      benefitPayer: values.benefitPayer,
      benefitPayerFee: values.benefitPayerFee,
      ...(confirm === undefined ? {} : { confirmed: confirm }),
    }
  }

  function submitSettings(confirm?: boolean): void {
    setError(null)
    const body = settingsBody(current, confirm)
    if (body === null) {
      setError('hrPayrollSettings.errorPayoutDay')
      return
    }
    saveSettings.mutate({ body })
  }

  const rates: Array<[string, string]> = [
    ['pension', `${bpToPercent(data.parameters.pensionEmployeeBp)} / ${bpToPercent(data.parameters.pensionEmployerBp)}`],
    ['disability', `${bpToPercent(data.parameters.disabilityEmployeeBp)} / ${bpToPercent(data.parameters.disabilityEmployerBp)}`],
    ['sickness', bpToPercent(data.parameters.sicknessEmployeeBp)],
    ['health', bpToPercent(data.parameters.healthBp)],
    ['laborFund', bpToPercent(data.parameters.laborFundBp)],
    ['guaranteed', bpToPercent(data.parameters.guaranteedBenefitsBp)],
    ['tax', `${bpToPercent(data.parameters.taxFirstRateBp)} / ${bpToPercent(data.parameters.taxSecondRateBp)}`],
    ['threshold', formatPLN(data.parameters.taxThresholdGr)],
    ['taxCredit', formatPLN(data.parameters.taxCreditAnnualGr)],
    ['kup', `${formatPLN(data.parameters.kupBasicGr)} / ${formatPLN(data.parameters.kupRaisedGr)}`],
    ['youthRelief', formatPLN(data.parameters.youthReliefLimitGr)],
    ['ppk', `${bpToPercent(data.parameters.ppkEmployeeDefaultBp)} / ${bpToPercent(data.parameters.ppkEmployerDefaultBp)}`],
  ]

  /* Absencja chorobowa: stawki i pule dni z parametrów roku (ADR-0012). */
  const sickFacts: string[] = [
    t('hrPayrollSettings.sickRates', {
      rate: bpToPercent(data.parameters.sickPayRateBp),
      full: bpToPercent(data.parameters.sickPayFullRateBp),
    }),
    t('hrPayrollSettings.sickLimits', {
      limit: data.parameters.sickPayDaysLimit,
      senior: data.parameters.sickPaySeniorDaysLimit,
    }),
  ]

  return (
    <>
      <p className={styles.lead}>{t('hrPayrollSettings.lead', { year })}</p>



      {data.settings.confirmed ? null : (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('hrPayrollSettings.unconfirmed', { year })}
        </InfoBanner>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>{t('hrPayrollSettings.ratesTitle')}</h3>
          <StatusPill tone={data.settings.confirmed ? 'success' : 'warning'}>
            {t(
              data.settings.confirmed
                ? 'hrPayrollSettings.confirmed'
                : 'hrPayrollSettings.notConfirmed',
            )}
          </StatusPill>
        </div>
        <Card pad="md">
          <dl className={styles.rates}>
            {rates.map(([key, value]) => (
              <div key={key} className={styles.rate}>
                <dt className={styles.rateLabel}>{t(`hrPayrollSettings.rates.${key}`)}</dt>
                <dd className={styles.rateValue}>{value}</dd>
              </div>
            ))}
          </dl>
          <p className={styles.legal}>{data.parameters.legalBasis}</p>
        </Card>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('hrPayrollSettings.companyTitle')}</h3>
        <Card pad="md">
          <p className={styles.note}>{t('hrPayrollSettings.companyNote')}</p>
          {canManage ? (
            <>
              <div className={styles.formGrid}>
                <FormField
                  label={t('hrPayrollSettings.fields.accident')}
                  htmlFor={ids.accident}
                  hint={t('hrPayrollSettings.fields.accidentHint')}
                >
                  <Input
                    id={ids.accident}
                    value={current.accident}
                    onChange={(event) => setForm({ ...current, accident: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.cap')}
                  htmlFor={ids.cap}
                  hint={t('hrPayrollSettings.fields.capHint')}
                >
                  <Input
                    id={ids.cap}
                    value={current.cap}
                    onChange={(event) => setForm({ ...current, cap: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.minimumWage')}
                  htmlFor={ids.minimum}
                  hint={t('hrPayrollSettings.fields.minimumWageHint')}
                >
                  <Input
                    id={ids.minimum}
                    value={current.minimumWage}
                    onChange={(event) => setForm({ ...current, minimumWage: event.target.value })}
                  />
                </FormField>
                {/*
                  Kwoty wolne od potrąceń z zasiłków (art. 141 ustawy
                  emerytalnej) ogłasza komunikat Prezesa ZUS i waloryzuje od
                  1 marca — dlatego wpisuje je księgowość, jak minimalne
                  wynagrodzenie. Bez nich z zasiłku nie potrącamy niczego
                  i lista płac to zgłasza (Etap 36).
                */}
                <FormField
                  label={t('hrPayrollSettings.fields.benefitFreeAlimony')}
                  htmlFor={ids.benefitFreeAlimony}
                  hint={t('hrPayrollSettings.fields.benefitFreeAlimonyHint')}
                >
                  <Input
                    id={ids.benefitFreeAlimony}
                    value={current.benefitFreeAlimony}
                    onChange={(event) =>
                      setForm({ ...current, benefitFreeAlimony: event.target.value })
                    }
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.benefitFreeOther')}
                  htmlFor={ids.benefitFreeOther}
                  hint={t('hrPayrollSettings.fields.benefitFreeOtherHint')}
                >
                  <Input
                    id={ids.benefitFreeOther}
                    value={current.benefitFreeOther}
                    onChange={(event) =>
                      setForm({ ...current, benefitFreeOther: event.target.value })
                    }
                  />
                </FormField>
                {/*
                  Pora nocna obejmuje OSIEM godzin między 21.00 a 7.00
                  (art. 151(7) § 1 k.p.), a które osiem — ustala pracodawca
                  w regulaminie pracy. Od tego okna zależy dodatek z art. 151(8)
                  i stawka 100 % za nadgodziny w nocy (Etap 42).
                */}
                <FormField
                  label={t('hrPayrollSettings.fields.nightFrom')}
                  htmlFor={ids.nightFrom}
                  hint={t('hrPayrollSettings.fields.nightHint')}
                >
                  <Input
                    id={ids.nightFrom}
                    type="time"
                    value={current.nightFrom}
                    onChange={(event) => setForm({ ...current, nightFrom: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.nightTo')}
                  htmlFor={ids.nightTo}
                >
                  <Input
                    id={ids.nightTo}
                    type="time"
                    value={current.nightTo}
                    onChange={(event) => setForm({ ...current, nightTo: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.payoutDay')}
                  htmlFor={ids.payoutDay}
                  hint={t('hrPayrollSettings.fields.payoutDayHint')}
                >
                  <Input
                    id={ids.payoutDay}
                    value={current.payoutDay}
                    onChange={(event) => setForm({ ...current, payoutDay: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.deviation')}
                  htmlFor={ids.deviation}
                  hint={t('hrPayrollSettings.fields.deviationHint')}
                >
                  <Input
                    id={ids.deviation}
                    value={current.deviation}
                    onChange={(event) => setForm({ ...current, deviation: event.target.value })}
                  />
                </FormField>
              </div>
              <Checkbox
                label={t('hrPayrollSettings.fields.payoutNextMonth')}
                checked={current.payoutNextMonth}
                onChange={(event) =>
                  setForm({ ...current, payoutNextMonth: event.target.checked })
                }
              />
              <p className={styles.note}>{t('hrPayrollSettings.payoutNextMonthHint')}</p>
              <h4 className={styles.subTitle}>{t('hrPayrollSettings.benefitSection')}</h4>
              {sickFacts.map((fact) => (
                <p key={fact} className={styles.note}>
                  {fact}
                </p>
              ))}
              <Checkbox
                label={t('hrPayrollSettings.benefitPayer')}
                checked={current.benefitPayer}
                onChange={(event) => setForm({ ...current, benefitPayer: event.target.checked })}
              />
              <p className={styles.note}>{t('hrPayrollSettings.benefitPayerHint')}</p>
              {current.benefitPayer ? (
                <>
                  <Checkbox
                    label={t('hrPayrollSettings.benefitPayerFee')}
                    checked={current.benefitPayerFee}
                    onChange={(event) =>
                      setForm({ ...current, benefitPayerFee: event.target.checked })
                    }
                  />
                  <p className={styles.note}>{t('hrPayrollSettings.benefitPayerFeeHint')}</p>
                </>
              ) : null}
              {/* Odmowa zapisu ustawień firmy przy jej przyciskach, a nie nad stawkami (grupa 82). */}
              {error !== null ? (
                <InfoBanner tone="danger" className={styles.banner}>
                  {t(error)}
                </InfoBanner>
              ) : null}
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  onClick={() => submitSettings()}
                  disabled={saveSettings.isPending}
                >
                  {t('hrPayrollSettings.save')}
                </Button>
                <Button
                  onClick={() => submitSettings(!data.settings.confirmed)}
                  disabled={saveSettings.isPending}
                >
                  {t(
                    data.settings.confirmed
                      ? 'hrPayrollSettings.withdraw'
                      : 'hrPayrollSettings.confirm',
                  )}
                </Button>
              </div>
            </>
          ) : (
            <dl className={styles.rates}>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.accident')}</dt>
                <dd className={styles.rateValue}>
                  {data.settings.accidentBp === null
                    ? t('hrPayrollSettings.missing')
                    : bpToPercent(data.settings.accidentBp)}
                </dd>
              </div>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.cap')}</dt>
                <dd className={styles.rateValue}>
                  {data.parameters.annualSocialCapGr === null
                    ? t('hrPayrollSettings.missing')
                    : formatPLN(data.parameters.annualSocialCapGr)}
                </dd>
              </div>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.minimumWage')}</dt>
                <dd className={styles.rateValue}>
                  {data.parameters.minimumWageGr === null
                    ? t('hrPayrollSettings.missing')
                    : formatPLN(data.parameters.minimumWageGr)}
                </dd>
              </div>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.benefitPayer')}</dt>
                <dd className={styles.rateValue}>
                  {t(data.settings.benefitPayer ? 'common.yes' : 'common.no')}
                </dd>
              </div>
            </dl>
          )}
        </Card>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('hrPayrollSettings.payerTitle')}</h3>
        <Card pad="md">
          <p className={styles.note}>{t('hrPayrollSettings.payerNote')}</p>
          {canManage ? (
            <>
              <div className={styles.formGrid}>
                <FormField
                  label={t('hrPayrollSettings.fields.nip')}
                  htmlFor={ids.nip}
                  {...payerField('nip', 'hrPayrollSettings.fields.nipHint')}
                >
                  <Input
                    id={ids.nip}
                    value={payer.nip}
                    onChange={(event) => setPayerForm({ ...payer, nip: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.regon')}
                  htmlFor={ids.regon}
                  {...payerField('regon', 'hrPayrollSettings.fields.regonHint')}
                >
                  <Input
                    id={ids.regon}
                    value={payer.regon}
                    onChange={(event) => setPayerForm({ ...payer, regon: event.target.value })}
                  />
                </FormField>
                {/*
                  Urząd skarbowy PŁATNIKA — jego kod wchodzi do nagłówka
                  deklaracji PIT-4R i PIT-8AR składanych elektronicznie
                  (Etap 28). Urząd pracownika jest osobnym polem w aktach.
                */}
                <FormField
                  label={t('hrPayrollSettings.fields.taxOfficeCode')}
                  htmlFor={ids.taxOfficeCode}
                  {...payerField('taxOfficeCode', 'hrPayrollSettings.fields.taxOfficeCodeHint')}
                >
                  <Select
                    id={ids.taxOfficeCode}
                    value={payer.taxOfficeCode}
                    onChange={(event) =>
                      setPayerForm({ ...payer, taxOfficeCode: event.target.value })
                    }
                  >
                    <option value="">{t('hrPayrollSettings.fields.taxOfficePlaceholder')}</option>
                    {TAX_OFFICES.map((office) => (
                      <option key={office.code} value={office.code}>
                        {office.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                {/*
                  Trzy rachunki, których system nie zgadnie: firmowy jako
                  zleceniodawca, e-składka nadany przez ZUS i mikrorachunek
                  podatkowy urzędu skarbowego (Etap 19).
                */}
                <FormField
                  label={t('hrPayrollSettings.fields.bankAccount')}
                  htmlFor={ids.bankAccount}
                  {...payerField('bankAccount', 'hrPayrollSettings.fields.bankAccountHint')}
                >
                  <Input
                    id={ids.bankAccount}
                    value={payer.bankAccount}
                    onChange={(event) =>
                      setPayerForm({ ...payer, bankAccount: event.target.value })
                    }
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.zusAccount')}
                  htmlFor={ids.zusAccount}
                  {...payerField('zusAccount', 'hrPayrollSettings.fields.zusAccountHint')}
                >
                  <Input
                    id={ids.zusAccount}
                    value={payer.zusAccount}
                    onChange={(event) => setPayerForm({ ...payer, zusAccount: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.taxAccount')}
                  htmlFor={ids.taxAccount}
                  {...payerField('taxAccount', 'hrPayrollSettings.fields.taxAccountHint')}
                >
                  <Input
                    id={ids.taxAccount}
                    value={payer.taxAccount}
                    onChange={(event) => setPayerForm({ ...payer, taxAccount: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.ppkAccount')}
                  htmlFor={ids.ppkAccount}
                  {...payerField('ppkAccount', 'hrPayrollSettings.fields.ppkAccountHint')}
                >
                  <Input
                    id={ids.ppkAccount}
                    value={payer.ppkAccount}
                    onChange={(event) => setPayerForm({ ...payer, ppkAccount: event.target.value })}
                  />
                </FormField>
                <FormField
                  label={t('hrPayrollSettings.fields.termCode')}
                  htmlFor={ids.termCode}
                  hint={t('hrPayrollSettings.fields.termCodeHint')}
                >
                  <Select
                    id={ids.termCode}
                    value={payer.termCode}
                    onChange={(event) => setPayerForm({ ...payer, termCode: event.target.value })}
                  >
                    <option value="">{t('hrPayrollSettings.termCodes.none')}</option>
                    {ZUS_TERM_CODES.map((code) => (
                      <option key={code} value={code}>
                        {termCodeLabel(t, code, data.parameters)}
                      </option>
                    ))}
                    {/* Kod zapisany przed #10, którego nie ma w mapie — widać go, ale nie da się wybrać ponownie. */}
                    {payer.termCode !== '' && !isZusTermCode(payer.termCode) ? (
                      <option value={payer.termCode} disabled>
                        {t('hrPayrollSettings.termCodes.unknown', { code: payer.termCode })}
                      </option>
                    ) : null}
                  </Select>
                </FormField>
              </div>
              {payerError !== null ? (
                <div id={ids.payerBanner}>
                  <InfoBanner tone="danger" className={styles.banner}>
                    {t(payerError.key)}
                  </InfoBanner>
                </div>
              ) : null}
              <div className={styles.actions}>
                <Button
                  onClick={() => {
                    setPayerError(null)
                    savePayer.mutate(payer)
                  }}
                  disabled={savePayer.isPending}
                >
                  {t('hrPayrollSettings.savePayer')}
                </Button>
              </div>
            </>
          ) : (
            <dl className={styles.rates}>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.nip')}</dt>
                <dd className={styles.rateValue}>
                  {data.payer.nip ?? t('hrPayrollSettings.missing')}
                </dd>
              </div>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.regon')}</dt>
                <dd className={styles.rateValue}>
                  {data.payer.regon ?? t('hrPayrollSettings.missing')}
                </dd>
              </div>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.termCode')}</dt>
                <dd className={styles.rateValue}>
                  {data.settings.zusTermCode === null
                    ? t('hrPayrollSettings.missing')
                    : isZusTermCode(data.settings.zusTermCode)
                      ? termCodeLabel(t, data.settings.zusTermCode, data.parameters)
                      : t('hrPayrollSettings.termCodes.unknown', {
                          code: data.settings.zusTermCode,
                        })}
                </dd>
              </div>
              <div className={styles.rate}>
                <dt className={styles.rateLabel}>{t('hrPayrollSettings.fields.zusDeadline')}</dt>
                <dd className={styles.rateValue}>
                  {data.settings.zusDeadlineDay === null
                    ? t('hrPayrollSettings.zusDeadlineUnknown')
                    : t('hrPayrollSettings.zusDeadlineDay', { day: data.settings.zusDeadlineDay })}
                </dd>
              </div>
            </dl>
          )}
        </Card>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('hrPayrollSettings.declarationsTitle')}</h3>
        <p className={styles.note}>{t('hrPayrollSettings.declarationsNote')}</p>
        {profiles.data.items.length === 0 ? (
          <EmptyState title={t('hrPayrollSettings.declarationsEmpty')} />
        ) : (
          <Card pad="flush" className={styles.list}>
            <div className={styles.declarationHead}>
              <span>{t('hrPayrollSettings.columns.employee')}</span>
              <span>{t('hrPayrollSettings.columns.ppk')}</span>
              <span>{t('hrPayrollSettings.columns.pit2')}</span>
              <span>{t('hrPayrollSettings.columns.kup')}</span>
              <span>{t('hrPayrollSettings.columns.residence')}</span>
              <span />
            </div>
            {profiles.data.items.map((profile) => (
              <div key={profile.employeeId} className={styles.declarationRow}>
                <span className={styles.rowTitle}>{profile.employeeName}</span>
                <span className={styles.rowValue}>
                  {/* B2B, dzieło, student na zleceniu i „tylko zdrowotne" — PPK ich nie obejmuje. */}
                  {profile.ppkCovered === false
                    ? t('hrPayrollSettings.ppkNotApplicable')
                    : t(`hrPayrollSettings.ppkStatus.${profile.ppkStatus}`)}
                </span>
                <span className={styles.rowValue}>
                  {profile.taxCreditDivisor === null
                    ? t(
                        profile.pit2Filed
                          ? 'hrPayrollSettings.pit2FromFile'
                          : 'hrPayrollSettings.pit2None',
                      )
                    : t(`hrPayrollSettings.divisor.${profile.taxCreditDivisor}`)}
                </span>
                <span className={styles.rowValue}>
                  {kupLabel(t, profile, data.parameters.mandateKupBp)}
                </span>
                <span className={styles.rowValue}>
                  {profile.taxResidence === 'nierezydent'
                    ? t('hrPayrollSettings.residenceNonResident', {
                        country: profile.residenceCountry ?? '—',
                      })
                    : t('hrPayrollSettings.residenceResident')}
                </span>
                <span className={styles.rowAction}>
                  {canManage ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setProfileError(null)
                        setProfileFields([])
                        setEditing(profile)
                      }}
                    >
                      {t('hrPayrollSettings.edit')}
                    </Button>
                  ) : null}
                </span>
              </div>
            ))}
          </Card>
        )}
      </section>

      {editing !== null ? (
        <Modal
          open
          title={t('hrPayrollSettings.editTitle', { employee: editing.employeeName })}
          onClose={closeProfile}
        >
          {/* Odmowa zapisu profilu stoi w oknie, a nie pod jego nakładką (przegląd Fali 2). */}
          {profileError !== null ? (
            <div id={ids.profileBanner}>
              <InfoBanner tone="danger" className={styles.banner}>
                {t(profileError)}
              </InfoBanner>
            </div>
          ) : null}
          {/*
            Osoba, której PPK nie obejmuje (B2B, dzieło, student na zleceniu,
            zlecenie „tylko zdrowotne"), nie ma pól deklaracji PPK — ta sama
            reguła co silnik i kolumna PPK listy (weryfikacja Fali 3).
          */}
          {insuranceContractOf(editing) !== null &&
          !isPpkCovered(insuranceContractOf(editing) as ContractType, editing) ? (
            <p className={styles.note}>{t('hrPayrollSettings.ppkNoteNotCovered')}</p>
          ) : (
            <>
          <FormField label={t('hrPayrollSettings.columns.ppk')} htmlFor={ids.ppkStatus}>
            <Select
              id={ids.ppkStatus}
              value={editing.ppkStatus}
              onChange={(event) =>
                setEditing({ ...editing, ppkStatus: event.target.value as PpkStatus })
              }
            >
              {PPK_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`hrPayrollSettings.ppkStatus.${status}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('hrPayrollSettings.fields.ppkEmployee')}
            htmlFor={ids.ppkEmployee}
            {...profileField('ppkEmployeeBp', 'hrPayrollSettings.fields.ppkEmployeeHint')}
          >
            <Input
              id={ids.ppkEmployee}
              value={bpToPercent(editing.ppkEmployeeBp)}
              onChange={(event) =>
                setEditing({ ...editing, ppkEmployeeBp: percentToBp(event.target.value) })
              }
            />
          </FormField>
          <FormField
            label={t('hrPayrollSettings.fields.ppkEmployer')}
            htmlFor={ids.ppkEmployer}
            {...profileField('ppkEmployerBp', 'hrPayrollSettings.fields.ppkEmployerHint')}
          >
            <Input
              id={ids.ppkEmployer}
              value={bpToPercent(editing.ppkEmployerBp)}
              onChange={(event) =>
                setEditing({ ...editing, ppkEmployerBp: percentToBp(event.target.value) })
              }
            />
          </FormField>
            </>
          )}
          {/*
            Wariant kosztów pracowniczych (art. 22 ust. 2 PIT) dotyczy tylko
            stosunku pracy. Przy zleceniu i dziele koszty wynikają z ustawy
            (art. 22 ust. 9 pkt 3–4), a B2B nie jest listą płac (grupa 126).
          */}
          {insuranceContractOf(editing) === null ||
          isEmploymentContract(insuranceContractOf(editing) as ContractType) ? (
            <FormField label={t('hrPayrollSettings.columns.kup')} htmlFor={ids.kup}>
              <Select
                id={ids.kup}
                value={editing.kupVariant}
                onChange={(event) =>
                  setEditing({ ...editing, kupVariant: event.target.value as KupVariant })
                }
              >
                {KUP_VARIANTS.map((variant) => (
                  <option key={variant} value={variant}>
                    {t(`hrPayrollSettings.kupVariant.${variant}`)}
                  </option>
                ))}
              </Select>
            </FormField>
          ) : (
            <p className={styles.note}>
              {t(
                insuranceContractOf(editing) === 'b2b'
                  ? 'hrPayrollSettings.kupNoteB2b'
                  : 'hrPayrollSettings.kupNoteCivil',
              )}
            </p>
          )}
          <FormField
            label={t('hrPayrollSettings.columns.pit2')}
            htmlFor={ids.divisor}
            hint={t('hrPayrollSettings.fields.divisorHint')}
          >
            <Select
              id={ids.divisor}
              value={editing.taxCreditDivisor === null ? '' : String(editing.taxCreditDivisor)}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  taxCreditDivisor:
                    event.target.value === ''
                      ? null
                      : (Number(event.target.value) as TaxCreditDivisor),
                })
              }
            >
              <option value="">{t('hrPayrollSettings.divisorFromFile')}</option>
              {TAX_CREDIT_DIVISORS.map((divisor) => (
                <option key={divisor} value={divisor}>
                  {t(`hrPayrollSettings.divisor.${divisor}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('hrPayrollSettings.insuranceCode')}
            htmlFor={ids.insuranceCode}
            {...profileField('insuranceCode', 'hrPayrollSettings.insuranceCodeHint')}
          >
            <Input
              id={ids.insuranceCode}
              value={editing.insuranceCode ?? ''}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  insuranceCode:
                    event.target.value.trim() === '' ? null : event.target.value.trim(),
                })
              }
            />
          </FormField>
          {/*
            Kod wykonywanego zawodu (Etap 16): zgłoszenie ZUS ZUA wymaga go od
            16 maja 2021 r., a ze stanowiska wpisanego słowami nie da się go
            wyprowadzić — dlatego pyta o niego formularz.
          */}
          <FormField
            label={t('hrPayrollSettings.occupationCode')}
            htmlFor={ids.occupationCode}
            {...profileField('occupationCode', 'hrPayrollSettings.occupationCodeHint')}
          >
            <Input
              id={ids.occupationCode}
              value={editing.occupationCode ?? ''}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  occupationCode:
                    event.target.value.trim() === '' ? null : event.target.value.trim(),
                })
              }
            />
          </FormField>
          <FormField
            label={t('hrPayrollSettings.sickPayDaysBefore')}
            htmlFor={ids.sickDays}
            {...profileField('sickPayDaysBefore', 'hrPayrollSettings.sickPayDaysBeforeHint')}
          >
            <Input
              id={ids.sickDays}
              value={String(editing.sickPayDaysBefore)}
              onChange={(event) => {
                const parsed = Number(event.target.value.trim())
                setEditing({
                  ...editing,
                  sickPayDaysBefore: Number.isInteger(parsed) && parsed >= 0 ? parsed : 0,
                  sickPayDaysBeforeYear: year,
                })
              }}
            />
          </FormField>
          <Checkbox
            label={t('hrPayrollSettings.benefitFromFirstDay')}
            checked={editing.benefitFromFirstDay}
            onChange={(event) =>
              setEditing({ ...editing, benefitFromFirstDay: event.target.checked })
            }
          />
          <p className={styles.note}>{t('hrPayrollSettings.benefitFromFirstDayHint')}</p>
          <Checkbox
            label={t('hrPayrollSettings.fields.youthReliefWaived')}
            checked={editing.youthReliefWaived}
            onChange={(event) =>
              setEditing({ ...editing, youthReliefWaived: event.target.checked })
            }
          />
          {/*
            Ulga z art. 21 ust. 1 pkt 152–154: powrót z zagranicy, rodzina 4+
            albo pracujący senior. Płatnik stosuje ją na WNIOSEK pracownika,
            a limit ma wspólny z ulgą dla młodych (Etap 32, ADR-0033).
          */}
          <FormField
            label={t('hrPayrollSettings.fields.taxRelief')}
            htmlFor={ids.taxRelief}
            hint={t('hrPayrollSettings.fields.taxReliefHint')}
          >
            <Select
              id={ids.taxRelief}
              value={editing.taxReliefKind ?? ''}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  taxReliefKind:
                    event.target.value === '' ? null : (event.target.value as TaxReliefKind),
                })
              }
            >
              <option value="">{t('hrPayrollSettings.fields.taxReliefNone')}</option>
              {TAX_RELIEF_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`hrPayrollSettings.taxReliefKinds.${kind}`)}
                </option>
              ))}
            </Select>
          </FormField>
          {editing.taxReliefKind !== null ? (
            <FormField
              label={t('hrPayrollSettings.fields.taxReliefUntilYear')}
              htmlFor={ids.taxReliefYear}
              {...profileField('taxReliefUntilYear', 'hrPayrollSettings.fields.taxReliefUntilYearHint')}
            >
              <Input
                id={ids.taxReliefYear}
                inputMode="numeric"
                value={editing.taxReliefUntilYear === null ? '' : String(editing.taxReliefUntilYear)}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    taxReliefUntilYear:
                      event.target.value.trim() === '' ? null : Number(event.target.value),
                  })
                }
              />
            </FormField>
          ) : null}
          {/*
            Koszty autorskie 50 % (art. 22 ust. 9 pkt 3 PIT) dotyczą umów, w
            których wynagrodzenie obejmuje przeniesienie praw autorskich — więc
            i dzieła, i zlecenia. O tym, czy tak jest, rozstrzyga treść umowy.
          */}
          {insuranceContractOf(editing) === 'dzielo' || insuranceContractOf(editing) === 'zlecenie' ? (
            <Checkbox
              label={t('hrPayrollSettings.fields.authorCosts')}
              checked={editing.authorCosts === true}
              onChange={(event) =>
                setEditing({ ...editing, authorCosts: event.target.checked })
              }
            />
          ) : null}
          {/*
            Rezydencja podatkowa (Etap 48, ADR-0049). Nierezydent płaci podatek
            u źródła 20 % przychodu z art. 29 ust. 1 pkt 1 — ale tylko ze
            zlecenia i umowy o dzieło; umowa o pracę idzie zaliczką jak
            u rezydenta. O samej rezydencji rozstrzygają fakty spoza listy płac
            (art. 3 ust. 1a), więc pytamy o nią wprost.
          */}
          <FormField
            label={t('hrPayrollSettings.fields.taxResidence')}
            htmlFor={ids.taxResidence}
            hint={t('hrPayrollSettings.fields.taxResidenceHint')}
          >
            <Select
              id={ids.taxResidence}
              value={editing.taxResidence ?? 'rezydent'}
              onChange={(event) =>
                setEditing({ ...editing, taxResidence: event.target.value as TaxResidence })
              }
            >
              {TAX_RESIDENCES.map((residence) => (
                <option key={residence} value={residence}>
                  {t(`hrPayrollSettings.taxResidences.${residence}`)}
                </option>
              ))}
            </Select>
          </FormField>
          {editing.taxResidence === 'nierezydent' ? (
            <>
              <FormField
                label={t('hrPayrollSettings.fields.residenceCountry')}
                htmlFor={ids.residenceCountry}
                {...profileField('residenceCountry', 'hrPayrollSettings.fields.residenceCountryHint')}
              >
                <Input
                  id={ids.residenceCountry}
                  value={editing.residenceCountry ?? ''}
                  maxLength={2}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      residenceCountry:
                        event.target.value.trim() === ''
                          ? null
                          : event.target.value.trim().toUpperCase(),
                    })
                  }
                />
              </FormField>
              <FormField
                label={t('hrPayrollSettings.fields.foreignTin')}
                htmlFor={ids.foreignTin}
                {...profileField('foreignTin', 'hrPayrollSettings.fields.foreignTinHint')}
              >
                <Input
                  id={ids.foreignTin}
                  value={editing.foreignTin ?? ''}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      foreignTin:
                        event.target.value.trim() === '' ? null : event.target.value.trim(),
                    })
                  }
                />
              </FormField>
              {/*
                Certyfikat rezydencji: bez niego stawka z umowy o unikaniu
                podwójnego opodatkowania nie wchodzi w grę (art. 29 ust. 2),
                a decyduje dzień WYPŁATY, nie dzień zawarcia umowy.
              */}
              <FormField
                label={t('hrPayrollSettings.fields.certificateFrom')}
                htmlFor={ids.certificateFrom}
              >
                <Input
                  id={ids.certificateFrom}
                  type="date"
                  value={editing.residenceCertificateFrom ?? ''}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      residenceCertificateFrom:
                        event.target.value === '' ? null : event.target.value,
                    })
                  }
                />
              </FormField>
              <FormField
                label={t('hrPayrollSettings.fields.certificateTo')}
                htmlFor={ids.certificateTo}
                {...profileField('residenceCertificateTo', 'hrPayrollSettings.fields.certificateToHint')}
              >
                <Input
                  id={ids.certificateTo}
                  type="date"
                  value={editing.residenceCertificateTo ?? ''}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      residenceCertificateTo:
                        event.target.value === '' ? null : event.target.value,
                    })
                  }
                />
              </FormField>
              <FormField
                label={t('hrPayrollSettings.fields.treatyRate')}
                htmlFor={ids.treatyRate}
                {...profileField('treatyTaxBp', 'hrPayrollSettings.fields.treatyRateHint')}
              >
                <Input
                  id={ids.treatyRate}
                  inputMode="decimal"
                  value={
                    editing.treatyTaxBp === null ? '' : String(editing.treatyTaxBp / 100)
                  }
                  onChange={(event) => {
                    const raw = event.target.value.trim().replace(',', '.')
                    const parsed = Number(raw)
                    setEditing({
                      ...editing,
                      treatyTaxBp:
                        raw === '' || Number.isNaN(parsed)
                          ? null
                          : Math.round(Math.min(20, Math.max(0, parsed)) * 100),
                    })
                  }}
                />
              </FormField>
            </>
          ) : null}
          {insuranceContractOf(editing) === 'zlecenie' ? (
            <>
              {/*
                Zakres ubezpieczenia (#80, D92): makieta go nie zna, a bez niego
                gałąź ZUS ZZA była nieosiągalna. Student do 26 lat nie ma żadnego
                tytułu, więc przy nim pola nie ma.
              */}
              {scopeVisible(editing) ? (
                <FormField
                  label={t('hrPayrollSettings.fields.insuranceScope')}
                  htmlFor={ids.insuranceScope}
                  hint={t('hrPayrollSettings.fields.insuranceScopeHint')}
                >
                  <Select
                    id={ids.insuranceScope}
                    value={editing.insuranceScope ?? 'pelne'}
                    onChange={(event) =>
                      setEditing({ ...editing, insuranceScope: event.target.value as InsuranceScope })
                    }
                  >
                    {INSURANCE_SCOPES.map((scope) => (
                      <option key={scope} value={scope}>
                        {t(`hrPayrollSettings.insuranceScopes.${scope}`)}
                      </option>
                    ))}
                  </Select>
                </FormField>
              ) : null}
              {healthOnlyVisible(editing) ? null : (
                <Checkbox
                  label={t('hrPayrollSettings.fields.mandateSickness')}
                  checked={editing.mandateSicknessOptIn}
                  onChange={(event) =>
                    setEditing({ ...editing, mandateSicknessOptIn: event.target.checked })
                  }
                />
              )}
              <Checkbox
                label={t('hrPayrollSettings.fields.mandateStudent')}
                checked={editing.mandateStudentExempt}
                onChange={(event) =>
                  setEditing({ ...editing, mandateStudentExempt: event.target.checked })
                }
              />
            </>
          ) : null}
          <div className={styles.actions}>
            <Button variant="secondary" onClick={closeProfile}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => saveProfile.mutate(editing)} disabled={saveProfile.isPending}>
              {t('hrPayrollSettings.saveProfile')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
